import { Loading } from "components/ui/Loading/Loading";
import type {
  MatchingResultFilters,
  MatchingResultListItem,
  MatchingResultSort,
} from "hooks/use-matching-results-api";
import { List } from "lucide-react";
import type { FC } from "react";
import { MatchingResultsFilter } from "routes/matching/MatchingResultsPage/components/MatchingResultsFilter";
import { MatchingResultsPagination } from "routes/matching/MatchingResultsPage/components/MatchingResultsPagination";
import { MatchingResultsTable } from "routes/matching/MatchingResultsPage/components/MatchingResultsTable";

export type MatchingResultsPageProps = {
  results: MatchingResultListItem[];
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  error: Error | undefined;
  filters: MatchingResultFilters;
  sort: MatchingResultSort;
  onPageChange: (page: number) => void;
  onFiltersChange: (filters: MatchingResultFilters) => void;
  onSortChange: (sort: MatchingResultSort) => void;
};

/**
 * マッチング結果一覧ページ Presentation
 * @param {MatchingResultsPageProps} props コンポーネントProps
 * @return {JSX.Element} MatchingResultsPage コンポーネント
 */
export const MatchingResultsPage: FC<MatchingResultsPageProps> = ({
  results,
  total,
  page,
  limit,
  loading,
  error,
  filters,
  sort,
  onPageChange,
  onFiltersChange,
  onSortChange,
}) => {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <List className="w-6 h-6 text-base-content/70" />
        <div>
          <h1 className="text-2xl font-bold">マッチング結果一覧</h1>
          <p className="text-sm text-base-content/60 mt-0.5">
            全{total}件のマッチング結果
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <MatchingResultsFilter filters={filters} onChange={onFiltersChange} />
          <select
            className="select select-bordered select-sm"
            value={`${sort.field}_${sort.order}`}
            onChange={e => {
              const [field, order] = e.target.value.split("_") as [
                "created_at" | "total_score",
                "asc" | "desc",
              ];
              onSortChange({ field, order });
            }}
          >
            <option value="created_at_desc">新しい順</option>
            <option value="created_at_asc">古い順</option>
            <option value="total_score_desc">スコア高い順</option>
            <option value="total_score_asc">スコア低い順</option>
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loading size="lg" color="#34495E" />
          </div>
        ) : error ? (
          <div className="alert alert-error">
            <span>{error.message}</span>
          </div>
        ) : (
          <>
            <MatchingResultsTable results={results} />
            <MatchingResultsPagination
              page={page}
              total={total}
              limit={limit}
              onPageChange={onPageChange}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MatchingResultsPage;
